import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Autocomplete,
  Menu,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ContentCopy,
  PlaylistAdd,
  Business,
  Assignment,
  Visibility,
  MoreVert,
  Update,
  CheckCircle,
  FileCopy,
} from '@mui/icons-material';

import { sitesService } from '../../services/sitesService';
import { useSiteContext } from '../../hooks/useSiteContext';

// Types for Standard Tasks
interface StandardTask {
  id: number;
  task_name: string;
  auto_generated_name: string;
  display_name: string;
  task_code?: string;
  task_type: string;
  equipment: number;
  equipment_number: string;
  equipment_kks_code: string;
  equipment_name: string;
  description: string;
  risk_level: string;
  requires_isolation: boolean;
  estimated_duration_hours?: number;
  copied_from_task?: string;
  has_copied_tasks: boolean;
  version: string;
  created_at: string;
  updated_at: string;
}

interface Equipment {
  id: number;
  name: string;
  kks_code: string;
  equipment_number: string;
  site_name: string;
}

interface Site {
  id: number;
  name: string;
  code: string;
}

const StandardTaskManager: React.FC = () => {
  const { currentSite } = useSiteContext();
  const [standardTasks, setStandardTasks] = useState<StandardTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<StandardTask | null>(null);
  const [copyToSiteDialogOpen, setCopyToSiteDialogOpen] = useState(false);
  const [updateCopiesDialogOpen, setUpdateCopiesDialogOpen] = useState(false);

  // Copy options
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment[]>([]);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [availableEquipment, setAvailableEquipment] = useState<Equipment[]>([]);
  const [availableSites, setAvailableSites] = useState<Site[]>([]);
  const [includeTaskDefinitions, setIncludeTaskDefinitions] = useState(true);
  const [includeSafetyMeasures, setIncludeSafetyMeasures] = useState(true);

  // Filters
  const [filters, setFilters] = useState<{
    task_type?: string;
    risk_level?: string;
    search?: string;
    show_templates_only?: boolean;
  }>({});

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTaskId, setMenuTaskId] = useState<number | null>(null);

  // Load standard tasks
  const loadStandardTasks = async () => {
    if (!currentSite.siteId) return;

    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams({
        site_id: currentSite.siteId?.toString() || '',
        page: (page + 1).toString(),
        page_size: rowsPerPage.toString(),
      });

      if (filters.task_type) params.append('task_type', filters.task_type);
      if (filters.risk_level) params.append('risk_level', filters.risk_level);
      if (filters.search) params.append('search', filters.search);

      // Use templates endpoint if showing templates only
      const endpoint = filters.show_templates_only 
        ? `/api/v1/sites/standard-tasks/templates/?${params.toString()}`
        : `/api/v1/sites/standard-tasks/?${params.toString()}`;

      const response = await fetch(endpoint, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setStandardTasks(data.results || data);
    } catch (err) {
      console.error('Failed to load standard tasks:', err);
      setError('Failed to load standard tasks');
    } finally {
      setLoading(false);
    }
  };

  // Load available equipment for copying
  const loadAvailableEquipment = async () => {
    try {
      const equipment = await sitesService.getEquipment();
      setAvailableEquipment(equipment as Equipment[]);
    } catch (err) {
      console.error('Failed to load equipment:', err);
    }
  };

  // Load available sites for copying
  const loadAvailableSites = async () => {
    try {
      const sites = await sitesService.getSites();
      setAvailableSites(sites);
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  };

  useEffect(() => {
    loadStandardTasks();
  }, [currentSite.siteId, page, rowsPerPage, filters]);

  useEffect(() => {
    loadAvailableEquipment();
    loadAvailableSites();
  }, []);

  // Handle copy to equipment
  const handleCopyToEquipment = async () => {
    if (!selectedTask || selectedEquipment.length === 0) return;

    try {
      const response = await fetch(`/api/v1/sites/standard-tasks/${selectedTask.id}/copy_to_equipment/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          equipment_ids: selectedEquipment.map(eq => eq.id),
          include_task_definitions: includeTaskDefinitions,
          include_safety_measures: includeSafetyMeasures,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Show success message
      console.log(`Successfully copied task to ${result.tasks_created} equipment items`);
      
      setCopyDialogOpen(false);
      setSelectedTask(null);
      setSelectedEquipment([]);
      loadStandardTasks();
    } catch (err) {
      console.error('Failed to copy task:', err);
      setError('Failed to copy task to equipment');
    }
  };

  // Handle copy to site
  const handleCopyToSite = async () => {
    if (!selectedTask || !selectedSite) return;

    try {
      const response = await fetch(`/api/v1/sites/standard-tasks/${selectedTask.id}/copy_to_site/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          site_id: selectedSite.id,
          include_task_definitions: includeTaskDefinitions,
          include_safety_measures: includeSafetyMeasures,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Show success message
      console.log(`Successfully copied task to ${result.tasks_created} equipment items in ${result.target_site}`);
      
      setCopyToSiteDialogOpen(false);
      setSelectedTask(null);
      setSelectedSite(null);
      loadStandardTasks();
    } catch (err) {
      console.error('Failed to copy task to site:', err);
      setError('Failed to copy task to site');
    }
  };

  // Handle update copied tasks
  const handleUpdateCopiedTasks = async () => {
    if (!selectedTask) return;

    try {
      const response = await fetch(`/api/v1/sites/standard-tasks/${selectedTask.id}/update_copied_tasks/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          update_version: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Show success message
      console.log(`Successfully updated ${result.tasks_updated} copied tasks`);
      
      setUpdateCopiesDialogOpen(false);
      setSelectedTask(null);
      loadStandardTasks();
    } catch (err) {
      console.error('Failed to update copied tasks:', err);
      setError('Failed to update copied tasks');
    }
  };

  const openCopyDialog = (task: StandardTask) => {
    setSelectedTask(task);
    setCopyDialogOpen(true);
  };

  const openCopyToSiteDialog = (task: StandardTask) => {
    setSelectedTask(task);
    setCopyToSiteDialogOpen(true);
  };

  const openUpdateCopiesDialog = (task: StandardTask) => {
    setSelectedTask(task);
    setUpdateCopiesDialogOpen(true);
  };

  const getTaskTypeColor = (taskType: string) => {
    switch (taskType) {
      case 'inspection': return 'primary';
      case 'maintenance': return 'secondary';
      case 'repair': return 'warning';
      case 'testing': return 'info';
      case 'calibration': return 'success';
      default: return 'default';
    }
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  if (!currentSite.siteId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a site to view standard tasks.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Standard Task Manager</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          // onClick={() => setCreateDialogOpen(true)}
        >
          Create Task
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search tasks..."
                value={filters.search || ''}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Task Type</InputLabel>
                <Select
                  value={filters.task_type || ''}
                  label="Task Type"
                  onChange={(e) => setFilters({ ...filters, task_type: e.target.value })}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="inspection">Inspection</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="repair">Repair</MenuItem>
                  <MenuItem value="testing">Testing</MenuItem>
                  <MenuItem value="calibration">Calibration</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Risk Level</InputLabel>
                <Select
                  value={filters.risk_level || ''}
                  label="Risk Level"
                  onChange={(e) => setFilters({ ...filters, risk_level: e.target.value })}
                >
                  <MenuItem value="">All Levels</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.show_templates_only || false}
                    onChange={(e) => setFilters({ ...filters, show_templates_only: e.target.checked })}
                  />
                }
                label="Templates Only"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tasks Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Task Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Risk Level</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Template Info</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {standardTasks.map((task) => (
                    <TableRow key={task.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {task.display_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {task.equipment_kks_code} • {task.equipment_name}
                          </Typography>
                          {task.copied_from_task && (
                            <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                              📋 Copied from: {task.copied_from_task}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.task_type}
                          color={getTaskTypeColor(task.task_type) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.risk_level}
                          color={getRiskLevelColor(task.risk_level) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {task.estimated_duration_hours ? `${task.estimated_duration_hours}h` : 'Not set'}
                      </TableCell>
                      <TableCell>
                        <Box>
                          {task.has_copied_tasks && (
                            <Chip 
                              label="Has copies" 
                              size="small" 
                              color="info" 
                              icon={<FileCopy />}
                              sx={{ mb: 0.5 }}
                            />
                          )}
                                                  {parseFloat(task.version) > 1.0 && (
                          <Chip
                            label={`v${task.version}`} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Tooltip title="Copy to Equipment">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openCopyDialog(task)}
                            >
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy to Site">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => openCopyToSiteDialog(task)}
                            >
                              <Business />
                            </IconButton>
                          </Tooltip>
                          {task.has_copied_tasks && (
                            <Tooltip title="Update Copied Tasks">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => openUpdateCopiesDialog(task)}
                              >
                                <Update />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setAnchorEl(e.currentTarget);
                              setMenuTaskId(task.id);
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {standardTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No standard tasks found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={-1} // Unknown total count
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => setAnchorEl(null)}>
          <Edit sx={{ mr: 1 }} fontSize="small" />
          Edit Task
        </MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)}>
          <Visibility sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Copy to Equipment Dialog */}
      <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Copy Task to Equipment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Task: {selectedTask?.display_name}
            </Typography>
            
            <Autocomplete
              multiple
              options={availableEquipment}
              getOptionLabel={(option) => `${option.kks_code} - ${option.name}`}
              value={selectedEquipment}
              onChange={(_, newValue) => setSelectedEquipment(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Equipment"
                  placeholder="Search equipment..."
                />
              )}
              sx={{ mt: 2, mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={includeTaskDefinitions}
                  onChange={(e) => setIncludeTaskDefinitions(e.target.checked)}
                />
              }
              label="Include Task Definitions"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeSafetyMeasures}
                  onChange={(e) => setIncludeSafetyMeasures(e.target.checked)}
                />
              }
              label="Include Safety Measures"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCopyToEquipment} variant="contained">
            Copy Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy to Site Dialog */}
      <Dialog open={copyToSiteDialogOpen} onClose={() => setCopyToSiteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Copy Task to All Equipment in Site</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Task: {selectedTask?.display_name}
            </Typography>
            
            <Autocomplete
              options={availableSites}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              value={selectedSite}
              onChange={(_, newValue) => setSelectedSite(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Site"
                  placeholder="Search sites..."
                />
              )}
              sx={{ mt: 2, mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={includeTaskDefinitions}
                  onChange={(e) => setIncludeTaskDefinitions(e.target.checked)}
                />
              }
              label="Include Task Definitions"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeSafetyMeasures}
                  onChange={(e) => setIncludeSafetyMeasures(e.target.checked)}
                />
              }
              label="Include Safety Measures"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyToSiteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCopyToSite} variant="contained">
            Copy to Site
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Copied Tasks Dialog */}
      <Dialog open={updateCopiesDialogOpen} onClose={() => setUpdateCopiesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update All Copied Tasks</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Task: {selectedTask?.display_name}
            </Typography>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              This will update all tasks that were copied from this template with the current task details.
              The template version will be incremented.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateCopiesDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateCopiedTasks} variant="contained" color="primary">
            Update All Copies
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StandardTaskManager; 